'use client';

import {
  ACCOUNT_PAGES,
  type AccountPage,
} from '@eliadmin/core/src/components/nt/account/shell/account-pages';
import {
  AccountUser,
  LoyaltyBox,
  SignOutLink,
} from '@eliadmin/core/src/components/nt/account/shell/account-user';
import Link from '@eliadmin/core/src/components/link';
import { Icon } from '@eliadmin/core/src/components/nt/ui/icon';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/**
 * Figma „Item“ of the account navigation: 20 px icon + Body/L, padding 10 / 12, radius 12, gap 12.
 * The current page (Active=…) on #F6FBF7 in Body/L Bold #0E6A20 with a green icon and the caret
 * on the right; the others #0D110D with a #5D655D icon.
 */
function SidebarItem({ page, active }: { page: AccountPage; active: boolean }) {
  const { t } = useTranslations();

  return (
    <li>
      <Link
        href={page.href()}
        aria-current={active ? 'page' : undefined}
        className={cn(
          'flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors focus-visible:shadow-focus focus-visible:outline-none',
          active ? 'bg-[#F6FBF7] text-[#0E6A20]' : 'text-[#0D110D] hover:bg-[#F5F6F3]',
        )}
      >
        <Icon name={page.icon} size={20} className={active ? 'text-[#127A26]' : 'text-[#5D655D]'} />
        <span className={cn('min-w-0 flex-1', active ? 'nt-body-l-bold' : 'nt-body-l')}>
          {t(page.label)}
        </span>
        {active ? <Icon name="caret-right" size={16} className="text-[#127A26]" /> : null}
      </Link>
    </li>
  );
}

/**
 * Figma „Account / Sidebar“ (205:11128, variants Active=Profil / Heslo / Objednávky / Obľúbené):
 * white card, 1 px #E2E5DF, radius 16, padding 24 / 20, 16 px rows – the user, the loyalty box,
 * the four pages (4 px apart), a divider and „Odhlásiť sa“.
 */
export function AccountSidebar({ current }: { current?: AccountPage['key'] }) {
  const { t } = useTranslations();

  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-[#E2E5DF] bg-white px-5 py-6">
      <AccountUser />
      <LoyaltyBox />
      <nav aria-label={t('text1734')}>
        <ul role="list" className="flex flex-col gap-1">
          {ACCOUNT_PAGES.map(page => (
            <SidebarItem key={page.key} page={page} active={page.key === current} />
          ))}
        </ul>
      </nav>
      <hr className="border-[#E2E5DF]" />
      <SignOutLink className="px-3 py-1.5" />
    </div>
  );
}
